import { supabase } from '@/integrations/supabase/client';

// ── Types ───────────────────────────────────────────────────
interface LogParams {
    businessId: string;
    userId: string;
    userName: string;
    action: string;
    entityType: string;
    entityId?: string;
    entityLabel?: string;
    targetType?: string;
    targetId?: string;
    targetLabel?: string;
    metadata?: Record<string, unknown>;
}

// ── Fire-and-forget activity logger ─────────────────────────
export function logActivity(params: LogParams): void {
    // Non-blocking: don't await, don't throw
    Promise.resolve(
        supabase
            .from('activity_logs' as 'canvass_sessions') // cast to bypass generated types
            .insert({
                business_id: params.businessId,
                user_id: params.userId,
                user_name: params.userName,
                action: params.action,
                entity_type: params.entityType,
                entity_id: params.entityId ?? null,
                entity_label: params.entityLabel ?? null,
                target_type: params.targetType ?? null,
                target_id: params.targetId ?? null,
                target_label: params.targetLabel ?? null,
                metadata: params.metadata ?? {},
                created_at: new Date().toISOString(),
            } as never)
    )
        .then(({ error }) => {
            if (error) {
                console.error('[ActivityLog] Insert failed:', error.message);
            }
        })
        .catch((err: unknown) => {
            console.error('[ActivityLog] Unexpected error:', err);
        });
}

// ── Helper: resolve current user info ───────────────────────
// Returns { userId, userName } from Supabase Auth session OR storefront customer session.
export async function resolveUser(businessId: string): Promise<{
    userId: string;
    userName: string;
} | null> {
    try {
        // 1. Try auth session — works for dashboard Supabase Auth users
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
            const u = session.user;
            const meta = u.user_metadata as Record<string, unknown> | undefined;
            const name =
                (meta?.full_name as string) ||
                (meta?.name as string) ||
                u.email ||
                'Unknown';
            return { userId: u.id, userName: name };
        }

        // 2. Fallback: try getUser() (slower network call)
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
            const meta = user.user_metadata as Record<string, unknown> | undefined;
            const name =
                (meta?.full_name as string) ||
                (meta?.name as string) ||
                user.email ||
                'Unknown';
            return { userId: user.id, userName: name };
        }

        // 3. Fallback: storefront customer auth (OTP-based, stored in localStorage)
        const stored = localStorage.getItem(`customer_auth_${businessId}`);
        if (stored) {
            const parsed = JSON.parse(stored) as {
                customer?: { id?: string; given_name?: string; family_name?: string; email?: string };
            };
            const c = parsed.customer;
            if (c?.id) {
                const name = [c.given_name, c.family_name].filter(Boolean).join(' ') || c.email || 'Customer';
                return { userId: c.id, userName: name };
            }
        }

        return null;
    } catch (err) {
        console.error('[ActivityLog] resolveUser failed:', err);
        return null;
    }
}
